import { Colors } from "@analyticsMD/quil";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import FilterControl from "./filter-control";
import { css } from "@emotion/css";
import { deepPurple, grey } from "@mui/material/colors";
import ListFilter from "../../types/ListFilter";

const sxStyles = {
  filterButton: {
    width: "6.75rem",
    height: "2.5rem",
    backgroundColor: grey[300],
    borderRadius: "4px",
    boxShadow: `1px 1px 5px 0 ${Colors.blackShadow}`,
    border: "none",
    color: Colors.grey_65,
    textTransform: "none",
    justifyContent: "space-between",
    "&:hover": {
      backgroundColor: Colors.grey_10,
      border: "none",
    },
  },
  filterControlWrapper: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginLeft: "1.5rem",
    justifyContent: "space-between",
  },
  filterMenuItemWrapper: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
  },
  subLabel: { fontSize: "0.875rem", color: Colors.grey_30 },
  clearLabel: {
    textTransform: "none",
    color: deepPurple[600],
    marginLeft: "0.5rem",
    "&:hover": { background: "none" },
  },
};

interface FilterToolbarProps {
  filterOptions: ListFilter[];
  activeFilters?: ListFilter[];
  handleSetFilter: (newFilter: any, currentFilters: any[]) => void;
}

const FilterToolbar: FC<FilterToolbarProps> = ({
  filterOptions,
  activeFilters = [],
  handleSetFilter,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState<ListFilter[]>([]);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (
      activeFilters &&
      activeFilters.length > 0 &&
      selectedFilters.length === 0
    ) {
      setSelectedFilters(activeFilters);
    }
  }, [activeFilters, selectedFilters.length]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = useCallback(
    (filterValue, filter) => {
      // update the selected filter to include the value
      const selectedIdx = selectedFilters.findIndex((f) => f.id === filter.id);
      setSelectedFilters((prev) => {
        const updatedFilters = [...prev];

        const normalizedValue = filter.filter_type?.startsWith("multi")
          ? filterValue === "all"
            ? []
            : Array.isArray(filterValue)
            ? filterValue
            : [filterValue]
          : filterValue === "all"
          ? ""
          : filterValue;

        updatedFilters[selectedIdx] = {
          ...updatedFilters[selectedIdx],
          value: normalizedValue,
        };

        handleSetFilter(updatedFilters[selectedIdx], updatedFilters);
        return updatedFilters;
      });
    },
    [selectedFilters, handleSetFilter]
  );

  const clearFilters = () => {
    setSelectedFilters([]);
    handleSetFilter(null, []);
  };

  const handleSelectFilter = (filter: ListFilter) => {
    const newSelectedFilters = [...selectedFilters, filter];
    setSelectedFilters(newSelectedFilters);

    handleClose();
  };

  const generateFilterControls = () =>
    selectedFilters.map((filter, idx) => (
      <FilterControl
        filter={filter}
        handleFilterChange={handleFilterChange}
        key={filter.id}
      />
    ));

  const generateFilterOptions = () => {
    const unusedFilterOptions = filterOptions.filter(
      (fo) => selectedFilters.find((sf) => sf.field === fo.field) === undefined
    );

    return unusedFilterOptions.map((fo) => (
      <MenuItem
        key={fo.id}
        onClick={() => handleSelectFilter(fo)}
        sx={{ width: "18rem" }}
      >
        <Box sx={sxStyles.filterMenuItemWrapper}>
          <span>{fo.label}</span>
          <span className={css(sxStyles.subLabel)}>{fo.source_label}</span>
        </Box>
      </MenuItem>
    ));
  };

  return (
    <>
      <Box sx={sxStyles.filterControlWrapper}>{generateFilterControls()}</Box>
      <Box sx={{ alignSelf: "center" }}>
        <Button
          variant="outlined"
          onClick={handleClick}
          sx={sxStyles.filterButton}
        >
          + Add filter
        </Button>
        {selectedFilters.length > 0 && (
          <Button onClick={clearFilters} sx={sxStyles.clearLabel}>
            Remove filters
          </Button>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {generateFilterOptions()}
        </Menu>
      </Box>
    </>
  );
};

export default FilterToolbar;
